<template>
    <div class="flex flex-col items-center min-h-screen">
        <div class="flex flex-col items-center flex-1 w-full py-8">
            <div class="flex-grow mt-16">
                <header class="flex flex-col items-center flex-none max-w-2xl mx-auto mb-6">
                    <a class="focus:no-underline hover:no-underline" href="/budgets">
                        <logo-inverse size="lg"></logo-inverse>
                    </a>
                    <div class="px-16 mt-6 mb-2 font-medium text-center"></div>
                </header>
                <div class="max-w-2xl mx-auto">
                    <div class="lg:px-0 lg:py-0 flex flex-col items-center justify-center h-full px-4 py-10">
                        <h4 class="m-y-md md:w-1/2 w-full text-center">{{ $t('welcome.heading', { name: user.first_name }) }}</h4>
                        <p v-if="userOwnsOrganization" class="md:w-2/3 w-full pb-6 text-center"
                            v-html="$t('welcome.owner_message', {
                                organization: user.organization.name,
                                emphasizedOpen: '<span class=&quot;font-semibold&quot;>',
                                emphasizedClose: '</span>'
                            })">
                        </p>
                        <p v-else class="md:w-2/3 w-full pb-6 text-center"
                            v-html="$t('welcome.member_message', {
                                organization: user.organization.name,
                                emphasizedOpen: '<span class=&quot;font-semibold&quot;>',
                                emphasizedClose: '</span>'
                            })">
                        </p>
                        <div class="md:w-1/3 w-1/2 pb-10">
                            <svg-celebration></svg-celebration>
                        </div>
                        <div class="w-2/3 pt-2">
                            <template v-if="currentApplicationStatus === 'active' && organizationApplicationUserId">
                                <button @click="connectToApplication" class="btn btn-block btn-primary hover:bg-blue-600 hover:border-blue-600 bg-blue-500 border-blue-500" type="button">
                                    {{ $t('welcome.action_singular') }}
                                </button>
                            </template>
                            <button v-else-if="(currentApplicationStatus === 'active' && !organizationApplicationUserId) || currentApplicationStatus === 'installing'" class="btn btn-block btn-primary disabled hover:bg-blue-600 hover:border-blue-600 bg-blue-500 border-blue-500" type="button">
                                <span class="icon icon-spinner icon-lg icon-fw spin"></span>
                                {{ $t('welcome.installing') }}
                            </button>
                            <button v-else class="btn btn-block btn-primary disabled hover:bg-blue-600 hover:border-blue-600 bg-blue-500 border-blue-500" type="button">
                                {{ $t('dashboard.installation_failed') }}
                                <span v-tooltip="{ content: $t('dashboard.installation_failed_info'), placement: 'top' }" class="icon icon-question-circle icon-lg icon-fw cursor-pointer"></span>
                            </button>
                        </div>
                        <p class="pt-12 text-center">
                            {{ $t('welcome.logout_line') }}
                            <a @click="logout" class="text-blue-500" href="javascript:;">
                                {{ $t('auth.logout') }}
                            </a>
                        </p>
                    </div>
                </div>
            </div>
            <footer class="flex-none text-xs text-gray-700">
                <ul class="list-inline">
                    <li><a class="hover:text-blue-500 link-muted text-gray-700" href="/budgets">Prima pagină</a></li>
                    <li>|</li>
                    <li><a class="hover:text-blue-500 link-muted plain text-gray-700" href="/terms-and-conditions" target="_blank">Termene și condiții</a></li>
                    <li>|</li>
                    <li><a class="hover:text-blue-500 link-muted plain text-gray-700" href="/privacy-policy" target="_blank">Confidențialitate</a></li>
                    <li>|</li>
                    <li>{{ new Date().getFullYear() }} &copy; Reefkig Budgets</li>
                </ul>
            </footer>
        </div>
    </div>
</template>

<script>
import LogoInverse from '../../guest/budgets/partials/logo-inverse.vue';
import SvgCelebration from '../../svg/budgets/celebration.vue';

export default {
    components: {
        'logo-inverse': LogoInverse,
        'svg-celebration': SvgCelebration,
    },

    props: {
        user: {
            type: Object,
            required: true,
        },
        application: {
            type: Object,
            required: false,
        },
    },

    data() {
        return {
            currentApplicationStatus: this.application.status,
            organizationApplicationUserId: this.application.organization_application_user_id,
            form: new Form({
                organization_application_user_id: '',
                session_data: null,
            }),
            applicationInstance: null,
            applicationForm: new Form({
                user_id: '',
                token: '',
            }),
            logoutForm: new Form({}),
        };
    },

    computed: {
        userOwnsOrganization() {
            return this.user.id === this.user.organization.owner_id;
        },

        sessionData() {
            return window.Globals.sessionData ?? [];
        },
    },

    created() {
        this.listenForStatusUpdates();
    },

    methods: {
        connectToApplication() {
            this.form.organization_application_user_id = this.organizationApplicationUserId;
            this.form.session_data = this.sessionData;

            axios.post('/budgets/welcome/proceed')
                .then(() => Globals.post('/api/v1/authentication-token', this.form))
                .then(response => {
                    this.applicationInstance = axios.create({ baseURL: this.application.url, withCredentials: true });
                    this.applicationForm.user_id = response.user_access.user_id;
                    this.applicationForm.token = response.token;
                })
                .then(() => this.applicationInstance.post('/login', this.applicationForm))
                .then(() => window.location.href = this.application.url)
                .catch(error => {
                    const errors = this.form.errors.any() ? this.form.errors.flatten() : this.applicationForm.errors.flatten();
                    const message = errors.length > 0 ? errors[0] : this.$t('globals.unexpected_error');
                    window.toastr.clear();
                    window.toastr.error(message);
                })
                .finally(() => {
                    this.resetForms();
                    this.applicationInstance = null;
                });
        },

        logout() {
            Globals.post('/logout', this.logoutForm)
                .then(() => window.location.href = '/budgets')
                .catch(() => {});
        },

        listenForStatusUpdates() {
            window.Echo.private(`Admin.OrganizationApplication.${this.application.pivot_id}`)
                .listen('.application.status_updated', response => {
                    this.currentApplicationStatus = response.status;

                    if (this.currentApplicationStatus === 'active') {
                        axios.get('/api/v1/user-access', { params: { organization_application_id: this.application.pivot_id } })
                            .then(response => this.organizationApplicationUserId = response.data.id ? response.data.id : null)
                            .catch(() => {});
                    }
                });
        },

        resetForms() {
            this.form = new Form({
                organization_application_user_id: '',
                session_data: null,
            });
            this.applicationForm = new Form({
                user_id: '',
                token: '',
            });
        },
    },
};
</script>
